import { useState } from 'react';
import { Card, Button, Typography, Input, FormControl, Select, MenuItem, IconButton } from '@material-ui/core';
import { DeleteOutline, AddCircleOutline } from '@material-ui/icons';

const MAX_HORIZONS = 10;
const MIN_HORIZONS = 1;

/**
 * @function ProjectionSettingsCard
 * @description Card for configuring the shoreline projection: which future horizons (in years) to
 * project, which projection method to use, and (when shoreline dates are available) which
 * reference shoreline to project from.
 * @param {Object} props - Component properties.
 * @param {Function} props.t - Translation function.
 * @param {Function} props.onConfirm - Called with (horizons, method, referenceConfig) when confirmed.
 * @param {Array<Object>} [props.shorelineDates=[]] - Available shoreline dates for the "by date" reference mode.
 * @see {@link addHorizon}
 * @see {@link removeHorizon}
 * @see {@link updateHorizon}
 * **Called by:
 * @see {ShoreRAnalysisSteps}
 * @memberof module:ShoreR
 * @returns {JSX.Element} The projection settings form.
 */
const ProjectionSettingsCard = ({ t, onConfirm, shorelineDates = [] }) => {
  const [horizons, setHorizons] = useState([5, 10, 25, 50, 100]);
  const [method, setMethod] = useState('smoothed_linear_regression');
  const hasShorelineDates = shorelineDates.length > 0;
  const [referenceMode, setReferenceMode] = useState('latest');
  const [referenceDateIndex, setReferenceDateIndex] = useState(
    hasShorelineDates ? shorelineDates[shorelineDates.length - 1].index : 0,
  );

  /**
   * @function addHorizon
   * @description Appends a new horizon, doubling the last one, up to `MAX_HORIZONS`.
   * **Called by:
   * @see {ProjectionSettingsCard} - via the "Add horizon" button's onClick.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const addHorizon = () => {
    if (horizons.length >= MAX_HORIZONS) return;
    setHorizons([...horizons, horizons[horizons.lastIndexOf] * 2]);
  };

  /**
   * @function removeHorizon
   * @description Removes a horizon by index, as long as at least `MIN_HORIZONS` remain.
   * @param {Number} index - Index of the horizon to remove.
   * **Called by:
   * @see {ProjectionSettingsCard} - via each horizon row's delete icon.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const removeHorizon = (index) => {
    if (horizons.length <= MIN_HORIZONS) return;
    setHorizons(horizons.filter((_, i) => i !== index));
  };

  /**
   * @function updateHorizon
   * @description Updates a horizon's value by index.
   * @param {Number} index - Index of the horizon to update.
   * @param {String|Number} value - New horizon value (years).
   * **Called by:
   * @see {ProjectionSettingsCard} - via each horizon row's Input onChange.
   * @memberof module:ShoreR
   * @returns {void}
   */
  const updateHorizon = (index, value) => {
    const parsed = Number(value);
    if (isNaN(parsed)) return;
    const updated = [...horizons];
    updated[index] = parsed;
    setHorizons(updated);
  };

  return (
    <Card style={{ padding: 12, marginBottom: 5 }} elevation={3}>
      <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 8 }}>
        {t('forms.shoreR.projection.title')}
      </Typography>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <Typography style={{ fontSize: 13, marginBottom: 4 }}>{t('forms.shoreR.projection.horizonLabel')}</Typography>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {horizons.map((year, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Input
                  type="number"
                  value={year}
                  onChange={(e) => updateHorizon(index, e.target.value)}
                  inputProps={{ min: 1 }}
                  style={{ width: 80 }}
                />
                <Typography style={{ fontSize: 13 }}>{t('forms.shoreR.projection.yearUnit')}</Typography>
                {horizons.length > MIN_HORIZONS && (
                  <IconButton size="small" onClick={() => removeHorizon(index)} style={{ padding: 4 }}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                )}
              </div>
            ))}
          </div>
          {horizons.length < MAX_HORIZONS && (
            <IconButton size="small" color="primary" onClick={addHorizon} style={{ marginTop: 4 }}>
              <AddCircleOutline fontSize="small" />
            </IconButton>
          )}
        </div>

        <div>
          <Typography style={{ fontSize: 13, marginBottom: 4 }}>{t('forms.shoreR.projection.methodLabel')}</Typography>
          <FormControl style={{ minWidth: 200 }}>
            <Select value={method} onChange={(e) => setMethod(e.target.value)}>
              <MenuItem value="smoothed_linear_regression">
                {t('forms.shoreR.projection.methods.smoothed_linear_regression')}
              </MenuItem>
              <MenuItem value="smoothed_end_point_rate">
                {t('forms.shoreR.projection.methods.smoothed_end_point_rate')}
              </MenuItem>
            </Select>
          </FormControl>
        </div>

        {hasShorelineDates && (
          <div>
            <Typography style={{ fontSize: 13, marginBottom: 4 }}>
              {t('forms.shoreR.projection.referenceShorelineLabel')}
            </Typography>
            <FormControl style={{ minWidth: 200 }}>
              <Select value={referenceMode} onChange={(e) => setReferenceMode(e.target.value)}>
                <MenuItem value="latest">{t('forms.shoreR.projection.referenceShorelineModes.latest')}</MenuItem>
                <MenuItem value="byDate">{t('forms.shoreR.projection.referenceShorelineModes.byDate')}</MenuItem>
                <MenuItem value="yearlyMean">
                  {t('forms.shoreR.projection.referenceShorelineModes.yearlyMean')}
                </MenuItem>
              </Select>
            </FormControl>

            {referenceMode === 'byDate' && (
              <div style={{ marginTop: 8 }}>
                <Typography style={{ fontSize: 13, marginBottom: 4 }}>
                  {t('forms.shoreR.projection.referenceShorelineDateLabel')}
                </Typography>
                <FormControl style={{ minWidth: 200 }}>
                  <Select value={referenceDateIndex} onChange={(e) => setReferenceDateIndex(Number(e.target.value))}>
                    {shorelineDates.map((d) => (
                      <MenuItem key={d.index} value={d.index}>
                        {d.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            )}
          </div>
        )}

        <Button
          color="primary"
          disabled={horizons.length < MIN_HORIZONS}
          style={{ justifyContent: 'left' }}
          onClick={() => {
            const referenceConfig = hasShorelineDates
              ? {
                  mode: referenceMode,
                  dateIndex: referenceMode === 'byDate' ? referenceDateIndex : undefined,
                }
              : { mode: 'latest' };
            onConfirm(horizons, method, referenceConfig);
          }}>
          {t('forms.shoreR.projection.confirm')}
        </Button>
      </div>
    </Card>
  );
};

export default ProjectionSettingsCard;
